import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { trace } from 'firebase/performance';
import '../../../styles/admin/UsersPending.css';
import BodyPending from './BodyPending.tsx';
import { IoSearchSharp, IoSettingsOutline } from 'react-icons/io5';
import { Users } from '../../../models/User.tsx';
import { AnyBet, editBetByField, showPlayersBets } from '../../../services/admin/BetsService.tsx';
import { addBetToAvailable, editUser } from '../../../services/admin/UserService.tsx';
import { addTransaction } from '../../../services/admin/Transaction.tsx';
import { FaArrowDownWideShort } from 'react-icons/fa6';
import { IoIosClose } from 'react-icons/io';
import { BetDetails } from '../../../models/MyBetModel.tsx';
import { Timestamp } from 'firebase/firestore';
import { formatNumbers, isFreePlayBet } from '../../../helpers/functions.tsx';
import { isSameAdminFiguresCalendarDay } from '../../../helpers/adminFiguresTime.tsx';
import { logUserActivity } from '../../../services/player/LoginService.tsx';
import { useUser } from '../../../context/UserContext.tsx';
import AgentContext from '../../../context/admin/Agent.tsx';
import { livePinLookup, withLivePlayerPin } from '../../../helpers/liveUserPin.ts';
import { FaSyncAlt } from 'react-icons/fa';
import { perf } from '../../../services/Firebase.js';

type SearchInput = 'agent' | 'player';
type FilterKey = 'agentID' | 'password' | 'timeAccepted' | 'timeScheduled';

const isParlay = (bet: AnyBet) => 'selections' in bet;
const hasBetName = (bet: AnyBet): bet is BetDetails => 'betName' in bet;

const valueMatchesQuery = (value: unknown, query: string): boolean => {
    if (typeof value === 'string') return value.toLowerCase().includes(query);
    if (typeof value === 'number') return value.toString().includes(query);
    if (value && typeof value === 'object') {
        return Object.values(value).some(inner => valueMatchesQuery(inner, query));
    }
    return false;
};

const betMatchesTextQuery = (bet: AnyBet, query: string) =>
    Object.values(bet).some(value => valueMatchesQuery(value, query));

const matchesAmount = (amount: number, selected: string) => {
    if (selected === 'any') return true;
    if (selected === '100+') return amount >= 100 && amount <= 499;
    if (selected === '500+') return amount >= 500 && amount <= 999;
    if (selected === '1000+') return amount >= 1000;
    return false;
};

const matchesType = (bet: AnyBet, selected: string) => {
    if (selected === 'allTypes') return true;
    if (selected === 'Free Play') return isFreePlayBet(bet);
    return bet.betType === selected;
};

const matchesTime = (betDate: Date | null, selected: string) => {
    if (!betDate) return false;
    if (selected === 'all') return true;

    const today = new Date();
    if (selected === 'today') return isSameAdminFiguresCalendarDay(betDate, today);

    const daysDiff = (today.getTime() - betDate.getTime()) / (1000 * 60 * 60 * 24);
    if (selected === '3days') return daysDiff <= 3;
    if (selected === '7days') return daysDiff <= 7;
    if (selected === '14days') return daysDiff <= 14;
    return false;
};

const filterBetsByCriteria = (
    bets: AnyBet[],
    selectedAmount: string,
    selectedType: string,
    selectedTime: string,
) =>
    bets.filter(bet => {
        if (typeof bet.bet !== 'number' || !matchesAmount(bet.bet, selectedAmount)) return false;
        if (!matchesType(bet, selectedType)) return false;
        const betDate = bet.betDate ? bet.betDate.toDate() : null;
        return matchesTime(betDate, selectedTime);
    });

const filterUsers = (users: Users[], query: string) => {
    const text = query.toLowerCase().trim();
    if (!text) return [];

    return users.filter(user => {
        const userName = (user.userName || '').toLowerCase();
        const name = (user.name || '').toLowerCase();
        const pin = String(user.pin || '').toLowerCase();
        return userName.includes(text) || name.includes(text) || pin.includes(text);
    });
};

const toSortMs = (value: any) => {
    if (value?.seconds) return value.seconds * 1000;
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    return 0;
};

const REPORT_FILTERS: { key: FilterKey; label: string; id: string }[] = [
    { key: 'agentID', label: 'Agent ID', id: 'checkAgent' },
    { key: 'password', label: 'Password', id: 'checkPass' },
    { key: 'timeAccepted', label: 'Time Accepted', id: 'checkAccepted' },
    { key: 'timeScheduled', label: 'Time Scheduled', id: 'checkScheduled' },
];

const UserSuggestionList = ({ users, onSelect, className = 'list-group', }: {
    users: Users[];
    onSelect: (user: Users) => void;
    className?: string;
}) => (
    <ul className={className}>
        {users.map(user => (
            <li key={user.id} className="list-group-item list-group-item-action" onClick={() => onSelect(user)}>
                <div className="d-flex justify-content-between">
                    <div>
                        <span className="search-username-navbar">{user.userName}</span>
                        <span className="search-pin-name-navbar">({user.pin})</span>
                    </div>
                    <div>
                        <span className="search-pin-name-navbar">{user.name}</span>
                    </div>
                </div>
            </li>
        ))}
    </ul>
);

const UsersPending = () => {
    const { user: currentUser, userIpData: userIpDataAdmin, updateUser } = useUser();
    const { allAgents, allPlayers } = useContext(AgentContext);

    const [bets, setBets] = useState<AnyBet[]>([]);
    const [searchResults, setSearchResults] = useState<AnyBet[]>([]);
    const [selectedTime, setSelectedTime] = useState(currentUser?.filters?.pendingTime ?? 'all');
    const [selectedAmount, setSelectedAmount] = useState(currentUser?.filters?.pendingAmount ?? 'any');
    const [selectedType, setSelectedType] = useState(currentUser?.filters?.pendingType ?? 'allTypes');
    const [filters, setFilters] = useState<Users['filters'] | undefined>(currentUser?.filters);
    const filtersRef = useRef(filters);
    filtersRef.current = filters;
    const [sortOption, setSortOption] = useState('risk');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeInput, setActiveInput] = useState<SearchInput | null>(null);
    const [agentName, setAgentName] = useState('');
    const [playerName, setPlayerName] = useState('');
    const [agentResults, setAgentResults] = useState<Users[]>([]);
    const [playerResults, setPlayerResults] = useState<Users[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedBet, setSelectedBet] = useState<AnyBet | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
        if (!currentUser?.userName) return;

        let cancelled = false;
        if (currentUser.filters) {
            setFilters(currentUser.filters);
            if (currentUser.filters.pendingTime) setSelectedTime(currentUser.filters.pendingTime);
            if (currentUser.filters.pendingAmount) setSelectedAmount(currentUser.filters.pendingAmount);
            if (currentUser.filters.pendingType) setSelectedType(currentUser.filters.pendingType);
        }

        const loadPendingBets = async () => {
            setIsLoading(true);
            setLoadError(null);

            const t = trace(perf, 'loadPendingBets');
            t.start();

            try {
                const { bets: pending } = await showPlayersBets(currentUser);
                if (!cancelled) setBets(pending);
            } catch (error) {
                console.error(error);
                if (!cancelled) setLoadError('Could not load pending bets.');
            } finally {
                t.stop();
                if (!cancelled) setIsLoading(false);
            }
        };

        void loadPendingBets();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.userName, currentUser?.userType]);

    const textSearchResults = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return [];
        return bets.filter(bet => betMatchesTextQuery(bet, query));
    }, [searchQuery, bets]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === 'agent') {
            setAgentName(value);
            setPlayerName('');
            setAgentResults(filterUsers(allAgents, value));
            return;
        }

        setPlayerName(value);
        setAgentName('');
        setPlayerResults(filterUsers(allPlayers, value));
    };

    const handleSelectUser = (user: Users, inputType: SearchInput) => {
        if (inputType === 'agent') {
            setAgentName(user.userName);
            setAgentResults([]);
            return;
        }
        setPlayerName(user.userName);
        setPlayerResults([]);
    };

    const persistPendingFilter = useCallback((patch: Partial<NonNullable<Users['filters']>>) => {
        if (!currentUser?.id) return;
        const nextFilters = { ...(filtersRef.current ?? currentUser.filters), ...patch };
        filtersRef.current = nextFilters;
        setFilters(nextFilters);
        updateUser({ filters: nextFilters });
        void editUser(currentUser.id, { filters: nextFilters }).catch(error => {
            console.error('Failed to save pending bets filter:', error);
        });
    }, [currentUser, updateUser]);

    const handleFilterToggle = (filterKey: FilterKey) => {
        setFilters(prev => {
            if (!prev || typeof prev[filterKey] !== 'boolean') return prev;
            return { ...prev, [filterKey]: !prev[filterKey] };
        });
    };

    const handleApplyClick = async () => {
        if (!filters || !currentUser?.id) return;
        try {
            await editUser(currentUser.id, { filters });
            updateUser({ filters });
        } catch (error) {
            console.error('Failed to update filters:', error);
        }
    };

    const sortPlayers = useCallback((list: AnyBet[]) => {
        const direction = sortOrder === 'desc' ? 1 : -1;

        return [...list].sort((a, b) => {
            let valueA = 0;
            let valueB = 0;

            if (sortOption === 'risk') {
                valueA = Number(a.bet) || 0;
                valueB = Number(b.bet) || 0;
            } else if (sortOption === 'win') {
                valueA = a.reward || 0;
                valueB = b.reward || 0;
            } else if (sortOption === 'scheduled') {
                valueA = toSortMs(a.gameDate);
                valueB = toSortMs(b.gameDate);
            } else if (sortOption === 'accepted') {
                valueA = a.betDate?.seconds || 0;
                valueB = b.betDate?.seconds || 0;
            }

            return (valueA - valueB) * direction;
        });
    }, [sortOption, sortOrder]);

    const handleSearch = () => {
        setHasSearched(true);

        const agentQuery = agentName.trim().toLowerCase();
        const playerQuery = playerName.trim().toLowerCase();

        let scoped = bets;
        if (agentQuery) {
            scoped = bets.filter(bet => bet.player?.parent?.toLowerCase() === agentQuery);
        } else if (playerQuery) {
            scoped = bets.filter(bet => bet.player?.userName?.toLowerCase() === playerQuery);
        }

        setSearchResults(scoped);
    };

    const displayResults = useMemo(() => {
        const source = searchQuery.trim()
            ? textSearchResults
            : hasSearched
                ? searchResults
                : bets;
        return filterBetsByCriteria(source, selectedAmount, selectedType, selectedTime);
    }, [searchQuery, textSearchResults, hasSearched, searchResults, bets, selectedAmount, selectedType, selectedTime]);

    const pinLookup = useMemo(
        () => livePinLookup([...allPlayers, ...allAgents]),
        [allPlayers, allAgents]
    );
    const betsWithLivePins = useMemo(
        () => bets.map(bet => withLivePlayerPin(bet, pinLookup)),
        [bets, pinLookup]
    );
    const displayResultsWithLivePins = useMemo(
        () => displayResults.map(bet => withLivePlayerPin(bet, pinLookup)),
        [displayResults, pinLookup]
    );

    const isFilteredView = hasSearched
        || !!searchQuery.trim()
        || selectedTime !== 'all'
        || selectedAmount !== 'any'
        || selectedType !== 'allTypes';

    const { totalBet, totalRisk, totalWagers } = useMemo(() => ({
        totalBet: displayResults.reduce((sum, bet) => isFreePlayBet(bet) ? sum : sum + (bet.bet || 0), 0),
        totalRisk: displayResults.reduce((sum, bet) => sum + (bet.reward || 0), 0),
        totalWagers: displayResults.length,
    }), [displayResults]);

    const removeBetFromLists = (ticket: string) => {
        setBets(prev => prev.filter(bet => bet.ticket !== ticket));
        setSearchResults(prev => prev.filter(bet => bet.ticket !== ticket));
    };

    const confirmDelete = async () => {
        if (!selectedBet?.ticket || !selectedBet.player || selectedBet.bet === undefined) {
            alert('No player or bet selected');
            return;
        }

        try {
            const now = new Date();
            const gameDate = (selectedBet.gameDate as Timestamp | undefined)?.toDate?.() ?? now;
            const freePlay = isFreePlayBet(selectedBet);

            await editBetByField(selectedBet.ticket, {
                deleteBy: currentUser?.userName,
                dateDelete: now,
                descriptionDelete: now > gameDate
                    ? 'Bet removed after the match started'
                    : 'Bet removed before the match started',
            });

            await addBetToAvailable(
                selectedBet.player.userName,
                selectedBet.bet,
                freePlay ? { isFreePlay: true } : undefined,
            );

            await addTransaction({
                ticket: selectedBet.ticket,
                createDate: Timestamp.fromDate(now),
                agent: selectedBet.player.parent ?? '',
                customer: selectedBet.player.userName,
                transactionType: 'refund',
                description: `Refund - Ticket ${selectedBet.ticket}`,
                amount: selectedBet.bet,
                createBy: currentUser?.userName ?? '',
                userType: 'Player',
                transactionFree: freePlay,
                master: selectedBet.player.master,
            } as any);

            if (currentUser?.userName) {
                await logUserActivity(
                    currentUser.userName,
                    `bet eliminated: Ticket ${selectedBet.ticket} | Bet: ${selectedBet.betName}`,
                    userIpDataAdmin,
                );
            }

            alert('Bet deleted successfully');
            removeBetFromLists(selectedBet.ticket);
            setSelectedBet(null);
        } catch (error) {
            console.error(error);
            alert('Error deleting bet');
        }
    };

    if (isLoading && bets.length === 0) {
        return (
            <div className="pending-page-loader">
                <FaSyncAlt className="loader-icon-proyect" />
            </div>
        );
    }

    if (loadError && bets.length === 0) {
        return (
            <div className="pending-page-loader" style={{ flexDirection: 'column', gap: 12 }}>
                <div className="text-center px-3" style={{ maxWidth: 420, color: '#0A2240' }}>
                    {loadError}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="container-fluid">
                {loadError && (
                    <div className="alert alert-warning py-2 mt-2 mb-0">
                        <span>{loadError}</span>
                    </div>
                )}

                <div className="mt-2 body-pending-total">
                    <div className="userPendig-container mt-1">
                        <div className="row gy-2 centered-pending">
                            <div className="col-5-pending col-lg-6">
                                <div className="input-group input-group-sm flex-nowrap">
                                    {currentUser?.userType === 'masterAgent' && (
                                        <>
                                            <span className="input-group-text">Agents</span>
                                            <input
                                                className="inputTransaction"
                                                placeholder="Search"
                                                name="agent"
                                                value={agentName}
                                                onFocus={() => setActiveInput('agent')}
                                                onChange={handleInputChange}
                                                autoComplete="off"
                                            />
                                            {activeInput === 'agent' && agentResults.length > 0 && (
                                                <UserSuggestionList
                                                    users={agentResults}
                                                    onSelect={user => handleSelectUser(user, 'agent')}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="col-5-pending col-lg-6">
                                <div className="input-group input-group-sm flex-nowrap">
                                    <span className="input-group-text">Players</span>
                                    <input
                                        className="inputTransaction"
                                        placeholder="Players"
                                        name="player"
                                        value={playerName}
                                        onFocus={() => setActiveInput('player')}
                                        onChange={handleInputChange}
                                        autoComplete="off"
                                    />
                                    {activeInput === 'player' && playerResults.length > 0 && (
                                        <UserSuggestionList
                                            users={playerResults}
                                            className="list-group search-results"
                                            onSelect={user => handleSelectUser(user, 'player')}
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="col-5-pending col-lg-6">
                                <div className="input-group input-group-sm flex-nowrap">
                                    <select
                                        id="select-days"
                                        name="selectdays"
                                        className="selectTransaction"
                                        value={selectedTime}
                                        onChange={e => {
                                            const value = e.target.value;
                                            setSelectedTime(value);
                                            persistPendingFilter({ pendingTime: value });
                                        }}
                                    >
                                        <option value="all">All</option>
                                        <option value="today">Today</option>
                                        <option value="3days">3 Days</option>
                                        <option value="7days">7 Days</option>
                                        <option value="14days">14 Days</option>
                                    </select>
                                </div>
                            </div>

                            <div className="col-5-pending col-lg-6">
                                <div className="input-group input-group-sm flex-nowrap">
                                    <select
                                        id="select-amount"
                                        name="selectamount"
                                        className="selectTransaction"
                                        value={selectedAmount}
                                        onChange={e => {
                                            const value = e.target.value;
                                            setSelectedAmount(value);
                                            persistPendingFilter({ pendingAmount: value });
                                        }}
                                    >
                                        <option value="any">Any Amount</option>
                                        <option value="100+">$100+</option>
                                        <option value="500+">$500+</option>
                                        <option value="1000+">$1000+</option>
                                    </select>
                                </div>
                            </div>

                            <div className="col-5-pending col-lg-6">
                                <div className="input-group input-group-sm flex-nowrap">
                                    <select
                                        id="select-type"
                                        name="selecttype"
                                        className="selectTransaction"
                                        value={selectedType}
                                        onChange={e => {
                                            const value = e.target.value;
                                            setSelectedType(value);
                                            persistPendingFilter({ pendingType: value });
                                        }}
                                    >
                                        <option value="allTypes">All types</option>
                                        <option value="Straight">Straight</option>
                                        <option value="Parlay">Parlay</option>
                                        <option value="Teaser">Teaser</option>
                                        <option value="Contest">Contest</option>
                                        <option value="Free Play">Free Play</option>
                                    </select>
                                </div>
                            </div>

                            <div className="col-5-pending col-lg-6">
                                <div className="button-container-pending">
                                    <button className="button-pending" onClick={handleSearch}>
                                        <IoSearchSharp className="pending-icon" />Search
                                    </button>
                                    <button className="button-pending" data-bs-toggle="modal" data-bs-target="#pending">
                                        <IoSettingsOutline className="pending-icon" />Settings
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="pendng-result mt-2">
                            <div>
                                <button className="button-sort none" data-bs-toggle="modal" data-bs-target="#sort">
                                    <FaArrowDownWideShort />Sort
                                </button>
                                <span className="search">Search: </span>
                                <input
                                    id="searchTime"
                                    name="searchTime"
                                    type="text"
                                    className="input-users search"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    autoComplete="off"
                                />
                            </div>
                            <div className="custom-flex-container">
                                <span className="body-title padding">
                                    Wagers: <span className="fw-bold padding">{formatNumbers(totalWagers)}</span>
                                </span>
                                <span className="body-title">
                                    Risking: <span className="padding-risking">${formatNumbers(totalBet)}</span>
                                </span>
                                <span className="body-title padding">
                                    To win: <span className="fw-bold">${formatNumbers(totalRisk)}</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive p-1">
                        <BodyPending
                            customer={betsWithLivePins}
                            playerResult={displayResultsWithLivePins}
                            filters={filters}
                            sortPlayers={sortPlayers}
                            isLoading={false}
                            hasSearched={isFilteredView}
                            setSelectedBet={setSelectedBet}
                            currentUser={currentUser}
                        />
                    </div>
                </div>
            </div>

            <div className="modal fade modal-sm" id="pending" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-title-acount-admin">
                            <span className="font-account">Report Setting</span>
                            <IoIosClose className="close-icon" size={42} data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="container-account">
                            {REPORT_FILTERS.map(({ key, label, id }) => (
                                <div className="container-setting" key={key}>
                                    <span className="font-size">{label}</span>
                                    <div className="form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={id}
                                            checked={Boolean(filters?.[key])}
                                            onChange={() => handleFilterToggle(key)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="modal-footer d-flex justify-content-center">
                            <button type="button" className="btn btn-success" onClick={handleApplyClick} data-bs-dismiss="modal">
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade modal-sm" id="sort" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-title-acount-admin">
                            <span className="font-account">Sort Setting</span>
                            <IoIosClose className="close-icon" size={42} data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="modal-body">
                            <select
                                id="first-select"
                                className="form-select"
                                value={sortOption}
                                onChange={e => setSortOption(e.target.value)}
                            >
                                <option value="risk">Risk Amount</option>
                                <option value="win">Win Amount</option>
                                <option value="scheduled">Scheduled</option>
                                <option value="accepted">Accepted</option>
                            </select>
                            <select
                                id="second-select"
                                className="mt-2 form-select"
                                value={sortOrder}
                                onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')}
                            >
                                <option value="asc">Ascending</option>
                                <option value="desc">Descending</option>
                            </select>
                        </div>
                        <div className="modal-footer d-flex justify-content-center">
                            <button type="button" className="btn btn-success" data-bs-dismiss="modal">Apply</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="deletBet" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title fs-5">
                                <span>Do you want to remove bet?</span>
                            </h4>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="modal-body">
                            <div className="d-flex gap-2 align-items-center">
                                <span className="transactionManager2 text-dark fw-semibold">Bet:</span>
                                <span className="transactionManager2 text-dark fw-semibold">
                                    {selectedBet && hasBetName(selectedBet) && selectedBet.betName}
                                    {selectedBet && isParlay(selectedBet) && (
                                        <div className="parlay-lines">
                                            {selectedBet.selections?.map((selection, index) => (
                                                <div key={index}>{selection.betName}</div>
                                            ))}
                                        </div>
                                    )}
                                </span>
                            </div>
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button type="button" className="btn btn-outline-danger" data-bs-dismiss="modal">Cancel</button>
                            <button
                                type="button"
                                className="btn btn-outline-success"
                                onClick={confirmDelete}
                                data-bs-dismiss="modal"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UsersPending;
